// packages import
import { createContext, FC, Reducer, useContext, useEffect, useMemo, useReducer } from 'react'
import { Outlet } from 'react-router-dom'
// components import
import { Alert } from '../components/common/Alert'
// others
import {
  ExternalUserRoleRole,
  useFetchAllExternalUserRolesLazyQuery,
  useFetchAllLawTypesLazyQuery,
  useFetchAllUsersLazyQuery,
  UserRole
} from '../../generated'
import { CaseAction, CaseActionType, caseInitialState, caseReducer, CaseState } from '../components/cases/caseReducer'
import { GRAPHQL_QUERY_POLICY, GRAPHQL_QUERY_POLICY_CACHE_FIRST, UNABLE_TO_FETCH_ALL_CASE_TYPES } from '../constants'
import { CaseContextReducerType, ChildrenType } from '../interfaceTypes'
import { AppContext } from './AppContext'
import { ActionType } from './AppContextReducer'
import { AuthContext } from './AuthContext'

export const CaseContext = createContext<CaseContextReducerType>({
  ...caseInitialState,

  dispatch: () => {
    return
  }
})

export const CaseContextProvider: FC<ChildrenType> = ({ children }): JSX.Element => {
  const { isLoggedIn, currentUser } = useContext(AuthContext)
  const { currentPage, dispatch: reducerDispatch } = useContext(AppContext)
  const [caseState, caseDispatch] = useReducer<Reducer<CaseState, CaseAction>>(caseReducer, caseInitialState)

  const store = useMemo(() => ({ ...caseState, caseDispatch }), [caseState])
  const { allLawTypes, allUsers, allExternalUserRoles } = caseState

  const [fetchAllLawTypes] = useFetchAllLawTypesLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,

    onError() {
      Alert.error(UNABLE_TO_FETCH_ALL_CASE_TYPES)
      caseDispatch({ type: CaseActionType.SET_ALL_LAW_TYPES, allLawTypes: undefined })
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllLawTypes } = data

        if (fetchAllLawTypes) {
          const { lawTypes } = fetchAllLawTypes || {}
          caseDispatch({ type: CaseActionType.SET_ALL_LAW_TYPES, allLawTypes: lawTypes || [] })
        }
      }
    }
  })

  const [fetchAllUsers] = useFetchAllUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,

    onError() {
      caseDispatch({ type: CaseActionType.SET_ALL_USERS, allUsers: [] })
      reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: 1 })
      reducerDispatch({ type: ActionType.SET_FILTER_DATA_COUNT, filterDataCount: 0 })
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllUsers } = data

        if (fetchAllUsers) {
          const { users, response, pagination } = fetchAllUsers
          const { status } = response || {}
          const { limit } = pagination || {}

          if (status === 200 && users) {
            caseDispatch({ type: CaseActionType.SET_ALL_USERS, allUsers: users })
            // page && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: page })
            limit && reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: limit })
          }
        }
      }
    }
  })

  const [fetchAllExternalUserRoles] = useFetchAllExternalUserRolesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onCompleted(data) {
      if (data) {
        const {
          fetchAllExternalUserRoles: { response, externalUserRoles }
        } = data

        if (response) {
          const { status } = response

          if (status === 200 && externalUserRoles) {
            caseDispatch({ type: CaseActionType.SET_ALL_EXTERNAL_USER_ROLES, allExternalUserRoles: externalUserRoles })

            const judgeId = externalUserRoles.find(userRole => userRole?.role === ExternalUserRoleRole.Judge)?.id
            const clientId = externalUserRoles.find(userRole => userRole?.role === ExternalUserRoleRole.Client)?.id
            const adversaryId = externalUserRoles.find(
              userRole => userRole?.role === ExternalUserRoleRole.Adversary
            )?.id
            const contactId = externalUserRoles.find(userRole => userRole?.role === ExternalUserRoleRole.Contact)?.id

            if (judgeId && clientId && adversaryId && contactId) {
              caseDispatch({ type: CaseActionType.SET_JUDGE_ROLE_ID, judgeRoleId: judgeId })
              caseDispatch({ type: CaseActionType.SET_CLIENT_ROLE_ID, clientRoleId: clientId })
              caseDispatch({ type: CaseActionType.SET_ADVERSARY_ROLE_ID, adversaryRoleId: adversaryId })
              caseDispatch({ type: CaseActionType.SET_CONTACT_ROLE_ID, contactRoleId: contactId })
            }
          }
        }
      }
    }
  })

  useEffect(() => {
    if (isLoggedIn) {
      !allUsers?.length &&
        fetchAllUsers({
          variables: {
            userInput: {
              paginationOptions: {
                limit: 2000,
                page: currentPage
              },
              roles: [UserRole.Admin, UserRole.Attorney, UserRole.Paralegal, UserRole.Investigator]
            }
          }
        })
      !allLawTypes?.length && fetchAllLawTypes()
      !allExternalUserRoles.length && fetchAllExternalUserRoles()
    }
    // eslint-disable-next-line
  }, [currentUser])

  return (
    <CaseContext.Provider value={{ ...store, dispatch: caseDispatch }}>
      {children}

      <Outlet />
    </CaseContext.Provider>
  )
}
